import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MapPin, Loader2, ArrowRight, AtSign } from 'lucide-react';
import { FullBrandLogo } from '../components/Logo';
import NeighborhoodSelect from '../components/NeighborhoodSelect';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [neighborhoodId, setNeighborhoodId] = useState('');
  const [handle, setHandle] = useState(user?.handle || '');
  const [missingFields, setMissingFields] = useState({});

  useEffect(() => {
    const checkCompletion = async () => {
      try {
        const res = await fetch(`${API}/me/profile-completion`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (data.complete) {
            navigate('/dashboard');
            return;
          }
          setMissingFields(data.missing || {});
          if (data.handle) setHandle(data.handle);
          if (data.neighborhood_id) setNeighborhoodId(data.neighborhood_id);
        }
      } catch (err) {
        console.error('Profile completion check failed:', err);
      } finally {
        setChecking(false);
      }
    };
    checkCompletion();
  }, [getAuthHeaders, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!neighborhoodId) {
      toast.error('Please select your neighborhood.');
      return;
    }
    if (missingFields.handle && (!handle || handle.length < 3)) {
      toast.error('Handle must be at least 3 characters.');
      return;
    }

    setLoading(true);
    try {
      const body = { neighborhood_id: neighborhoodId };
      if (missingFields.handle) body.handle = handle;

      const res = await fetch(`${API}/me/complete-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success('Profile completed!');
        if (refreshUser) await refreshUser();
        navigate('/dashboard');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save. Try again.');
      }
    } catch {
      toast.error('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="complete-profile-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <FullBrandLogo size="lg" className="mx-auto mb-6 drop-shadow-xl" />
          <h1 className="font-heading text-3xl font-bold uppercase mb-2">Complete Your Profile</h1>
          <p className="text-muted-foreground">One more step to unlock the full DMV Soundstage experience</p>
        </div>

        <Card className="glass-card border-border p-6">
          <div className="flex items-center gap-3 p-3 mb-6 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <p className="text-sm">
              <span className="font-medium text-orange-400">Neighborhood is required</span> — it helps connect you with the local DMV community.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Handle (if missing) */}
            {missingFields.handle && (
              <div className="space-y-1.5">
                <Label htmlFor="cp-handle">@handle *</Label>
                <div className="relative">
                  <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="cp-handle" type="text" placeholder="yourhandle" value={handle}
                    onChange={e => setHandle(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
                    className="pl-10" required maxLength={30} data-testid="complete-handle-input" />
                </div>
              </div>
            )}

            {/* Neighborhood (always required here) */}
            <NeighborhoodSelect
              value={neighborhoodId}
              onChange={(id) => setNeighborhoodId(id)}
              required
            />

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-foreground font-bold h-11"
              disabled={loading} data-testid="complete-profile-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
